/**
 * Normalize Google Places address components from the legacy PlaceResult
 * (`address_components` / long_name) and the Places API (New)
 * (`addressComponents` / longText).
 *
 * @param {unknown} place
 * @returns {Array<{ long_name: string, short_name: string, types: string[] }>}
 */
export function getPlaceAddressComponents(place) {
  if (!place || typeof place !== 'object') return [];
  const raw = place.address_components || place.addressComponents || [];
  if (!Array.isArray(raw)) return [];
  return raw.map((comp) => {
    if (!comp || typeof comp !== 'object') {
      return { long_name: '', short_name: '', types: [] };
    }
    const types = Array.isArray(comp.types) ? comp.types : [];
    const longName = comp.long_name || comp.longText || '';
    const shortName = comp.short_name || comp.shortText || longName;
    return { long_name: longName, short_name: shortName, types };
  });
}

export function placeHasAddressComponents(place) {
  return getPlaceAddressComponents(place).length > 0;
}

export function getPlaceId(place) {
  if (!place || typeof place !== 'object') return '';
  return place.place_id || place.placeId || place.id || '';
}

/**
 * Parse Google Places address_components into fields (same logic as BasicInfo).
 * @param {google.maps.places.PlaceResult | { address_components?: Array<{ long_name: string, short_name: string, types: string[] }> }} place
 * @returns {{ street: string, city: string, province: string, postalCode: string, country: string, countryShort: string }}
 */
export function parseGooglePlaceAddress(place) {
  const components = getPlaceAddressComponents(place);
  const get = (type, short = false) => {
    const comp = components.find((c) => c.types.includes(type));
    if (!comp) return '';
    return short ? comp.short_name : comp.long_name;
  };
  const street = [get('street_number'), get('route')].filter(Boolean).join(' ').trim();
  const city =
    get('locality') ||
    get('sublocality_level_1') ||
    get('sublocality') ||
    get('postal_town') ||
    get('administrative_area_level_3') ||
    get('administrative_area_level_2') ||
    '';
  return {
    street,
    city,
    province: get('administrative_area_level_1', true),
    postalCode: get('postal_code'),
    country: get('country', false),
    countryShort: get('country', true),
  };
}
